/**
 * Settings · Email Audit (ARC-014).
 *
 * Read-only browse over every outbound communication attempt persisted
 * by `outbound_gateway`. Suppressed (Preview) and delivered
 * (Production) records both appear here so operators can verify what
 * *would* have been sent — the safety net for the Preview environment.
 *
 * Role gate is enforced by the backend (`Administrator` + `Owner`);
 * this page hides itself in the Workspace Registry for other roles.
 */
import { useEffect, useMemo, useState } from "react";
import {
  EnvelopeSimple, ShieldCheck, Prohibit, CheckCircle, X, ArrowClockwise,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function EmailAudit() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all"); // all | delivered | suppressed
  const [stats, setStats] = useState({ total: 0, delivered: 0, suppressed: 0 });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filter === "delivered") params.delivered = true;
      if (filter === "suppressed") params.delivered = false;
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/email-audit", { params }),
        api.get("/email-audit/stats/summary"),
      ]);
      setRows(list.items || []);
      setTotal(list.total || 0);
      setStats(s || { total: 0, delivered: 0, suppressed: 0 });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load email audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/email-audit/${id}`);
      setSelected(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load audit detail");
    }
  };

  return (
    <div className="space-y-6" data-testid="email-audit-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-ink-muted">
            Settings
          </div>
          <h1 className="font-display text-2xl font-semibold text-charcoal">
            Email Audit
          </h1>
          <p className="text-sm text-ink-muted max-w-2xl mt-1">
            Every outbound email attempt — delivered or suppressed — is
            recorded here. Preview and Development environments never
            transmit; the rendered message is captured so you can
            verify exactly what Production would have sent.
          </p>
        </div>
        <button
          data-testid="email-audit-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-md border border-divider bg-surface px-3 py-2 text-sm font-semibold text-ink-secondary hover:bg-surface-alt"
        >
          <ArrowClockwise size={14} weight="bold" /> Refresh
        </button>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Total attempts" value={stats.total}     tone="slate"    testid="email-audit-stat-total" />
        <StatCard label="Delivered"      value={stats.delivered} tone="emerald"  testid="email-audit-stat-delivered" icon={<CheckCircle size={18} weight="fill" />} />
        <StatCard label="Suppressed"     value={stats.suppressed} tone="amber"   testid="email-audit-stat-suppressed" icon={<Prohibit size={18} weight="fill" />} />
      </div>

      <div className="flex items-center gap-1 border-b border-divider" data-testid="email-audit-filters">
        {[
          { key: "all",        label: `All (${stats.total})` },
          { key: "delivered",  label: `Delivered (${stats.delivered})` },
          { key: "suppressed", label: `Suppressed (${stats.suppressed})` },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`email-audit-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`border-b-2 px-3 py-2 text-sm font-medium ${
              filter === f.key
                ? "border-brand text-brand"
                : "border-transparent text-ink-muted hover:text-ink-secondary"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div
          data-testid="email-audit-error"
          className="rounded-md border border-status-error/40 bg-status-error-bg px-3 py-2 text-sm text-status-error-fg"
        >
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-divider bg-surface">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-[11px] font-bold uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">Workflow</th>
              <th className="px-3 py-2 text-left">Recipients</th>
              <th className="px-3 py-2 text-left">Subject</th>
              <th className="px-3 py-2 text-left">Env</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-divider">
            {loading && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-ink-muted">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-ink-muted" data-testid="email-audit-empty">
                  No outbound email attempts recorded yet.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                data-testid={`email-audit-row-${r.id}`}
                className="cursor-pointer hover:bg-surface-alt"
                onClick={() => openDetail(r.id)}
              >
                <td className="px-3 py-2"><StatusBadge row={r} /></td>
                <td className="px-3 py-2 whitespace-nowrap text-ink-secondary">{fmtDT(r.created_at)}</td>
                <td className="px-3 py-2 font-mono text-[12px] text-ink-secondary">{r.workflow || "—"}</td>
                <td className="px-3 py-2 text-charcoal">
                  <div className="truncate max-w-[220px]" title={(r.recipients || []).join(", ")}>
                    {(r.recipients || []).join(", ") || "—"}
                  </div>
                </td>
                <td className="px-3 py-2 text-charcoal">
                  <div className="truncate max-w-[280px]" title={r.subject}>{r.subject || "(no subject)"}</div>
                </td>
                <td className="px-3 py-2 text-[11px] uppercase tracking-wider text-ink-muted">{r.environment}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    data-testid={`email-audit-open-${r.id}`}
                    className="text-brand hover:text-brand-hover text-sm font-semibold"
                  >
                    View →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <DetailDrawer detail={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function StatCard({ label, value, tone, icon, testid }) {
  const toneCls =
    tone === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-800" :
    tone === "amber"   ? "border-amber-200  bg-amber-50  text-amber-800" :
                         "border-divider bg-surface text-ink-secondary";
  return (
    <div data-testid={testid} className={`rounded-md border p-4 ${toneCls}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider">{label}</div>
      <div className="mt-1 flex items-center gap-2 text-2xl font-semibold text-charcoal">
        {icon} {value}
      </div>
    </div>
  );
}

function StatusBadge({ row }) {
  if (row.delivered) {
    return (
      <span data-testid="email-audit-status-delivered" className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
        <CheckCircle size={12} weight="fill" /> Delivered
      </span>
    );
  }
  return (
    <span data-testid="email-audit-status-suppressed" className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
      <Prohibit size={12} weight="fill" /> Suppressed
    </span>
  );
}

function DetailDrawer({ detail, onClose }) {
  const suppressed = !detail.delivered;
  return (
    <div className="fixed inset-0 z-40" data-testid="email-audit-drawer">
      <div className="absolute inset-0 bg-surface-inverse/40" onClick={onClose} />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col border-l border-divider bg-surface shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b border-divider p-4">
          <div>
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={18} weight="regular" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-ink-muted">
                {detail.environment} · {detail.channel || "email"} · {detail.workflow}
              </span>
            </div>
            <h2 className="mt-1 text-lg font-semibold text-charcoal" data-testid="email-audit-detail-subject">
              {detail.subject || "(no subject)"}
            </h2>
            {suppressed ? (
              <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="email-audit-detail-suppression-banner">
                <ShieldCheck size={16} weight="fill" className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">Suppressed — Preview environment</div>
                  <div className="text-xs">
                    {detail.suppression_reason || "Delivery disabled outside production."}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                <CheckCircle size={12} weight="fill" /> Delivered · {detail.provider || "microsoft_graph"}
              </div>
            )}
          </div>
          <button
            data-testid="email-audit-drawer-close"
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-secondary hover:bg-surface-alt"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <MetaGrid detail={detail} />

          {detail.body_html ? (
            <section data-testid="email-audit-detail-body-html">
              <SectionLabel>Rendered HTML</SectionLabel>
              <iframe
                title="rendered-html"
                srcDoc={detail.body_html}
                className="w-full min-h-[280px] rounded-md border border-divider bg-white"
              />
            </section>
          ) : null}

          {detail.body_text ? (
            <section data-testid="email-audit-detail-body-text">
              <SectionLabel>Rendered text</SectionLabel>
              <pre className="whitespace-pre-wrap rounded-md border border-divider bg-surface-alt p-3 font-mono text-[12px] text-charcoal">
                {detail.body_text}
              </pre>
            </section>
          ) : null}

          {detail.error && (
            <section>
              <SectionLabel>Provider error</SectionLabel>
              <pre className="whitespace-pre-wrap rounded-md border border-rose-200 bg-rose-50 p-3 font-mono text-[12px] text-rose-800">
                {detail.error}
              </pre>
            </section>
          )}
        </div>
      </aside>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-ink-muted">
      {children}
    </div>
  );
}

function MetaGrid({ detail }) {
  const rows = useMemo(() => ([
    ["From",            detail.sender || "—"],
    ["To",              (detail.recipients || []).join(", ") || "—"],
    ["Cc",              (detail.cc || []).join(", ") || "—"],
    ["Bcc",             (detail.bcc || []).join(", ") || "—"],
    ["Workflow",        detail.workflow || "—"],
    ["Triggering user", detail.triggering_user || "—"],
    ["Correlation id",  detail.correlation_id || "—"],
    ["Timestamp",       fmtDT(detail.created_at)],
  ]), [detail]);
  return (
    <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="email-audit-detail-meta">
      {rows.map(([k, v]) => (
        <div key={k} className="rounded-md border border-divider bg-surface-alt p-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">{k}</div>
          <div className="mt-0.5 break-words font-mono text-[12px] text-charcoal">{v}</div>
        </div>
      ))}
    </dl>
  );
}
